import "./warrantyList.css";
import { Close } from "@material-ui/icons";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    getWarranties,
    completeWarranty,
    deleteWarranty,
} from "../../redux/apiCalls";
import moment from "moment";
import { publicRequest } from "../../requestMethods";
import Select from "react-select";
import { commonSelectProps } from "../../components/shared/SelectStyles";
import { FiPlus } from "react-icons/fi";

const statusOptions = [
    {
        label: "Pending",
        value: "pending",
    },
    {
        label: "Approved",
        value: "approved",
    },
    {
        label: "Rejected",
        value: "rejected",
    },
    {
        label: "All",
        value: "",
    },
];

export default function WarrantyRequestList() {
    const dispatch = useDispatch();

    const currentUser = useSelector((state) => state.user.currentUser);
    const superadmin = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.isSuperAdmin === true : null
    );
    const manager = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "manager"
            : null
    );

    const staff = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.type === "staff" : null
    );

    const specialAccess = useSelector((state) =>
        state.user.currentUser
            ? ["64be53a291c85522064a711a", "6426333a94c2157f1ba818b9"].includes(
                  state.user.currentUser._id
              )
            : null
    );
    const principle = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.principle : null
    );
    const warranties = useSelector((state) => state.warranty.warranties);
    
    // Determine principle parameter based on user type
    const getPrincipleParam = () => {
        if (superadmin) {
            // SuperAdmin can see all warranty requests
            return undefined;
        } else if (currentUser?.type === "outright" || currentUser?.type === "manager") {
            // Outright and manager users can only see their own principle
            return currentUser?._id;
        } else {
            // For other user types, use their principle if available
            return currentUser?.principle;
        }
    };

    const [isLoaded, setIsLoaded] = useState(false);
    const [modalData, setModalData] = useState([]);
    const [warrantyData, setWarrantyData] = useState([]);
    const [returnWarrantyMenu, setReturnWarrantyMenu] = useState(false);
    const [completedWarrantyMenu, setCompletedWarrantyMenu] = useState(false);
    const [inputs, setInputs] = useState({
        model: "",
        userId: specialAccess
            ? "63429be7a4b0715d61b8955c"
            : manager
            ? principle
            : staff
            ? principle
            : undefined,
        limit: 20,
        page: 1,
        status: "pending",
        serialNumber: "",
        dateTo: undefined,
        dateFrom: undefined,
        principle: getPrincipleParam(),
    });
    const [usersData, setUsersData] = useState([]);

    const getUsers = async () => {
        const res = await publicRequest.get("/users/admin", {
            params: { isAdmin: true },
        });
        setUsersData(res.data);
    };

    useEffect(() => {
        if (returnWarrantyMenu === true) {
            window.scroll(0, 0);
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "inherit";
        }
    }, [returnWarrantyMenu]);

    useEffect(() => {
        if (completedWarrantyMenu === true) {
            window.scroll(0, 0);
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "inherit";
        }
    }, [completedWarrantyMenu]);

    useEffect(() => {
        // Update principle in inputs when currentUser changes
        const principleParam = getPrincipleParam();
        setInputs((prev) => {
            // Only update if principle actually changed to avoid unnecessary re-renders
            if (prev.principle !== principleParam) {
                return {
                    ...prev,
                    principle: principleParam,
                };
            }
            return prev;
        });
    }, [currentUser?._id, currentUser?.principle, currentUser?.type, superadmin]);

    useEffect(() => {
        const fetchData = async () => {
            if (isLoaded === false) {
                try {
                    const result = await getWarranties(dispatch, inputs);
                    setWarrantyData(result.users);
                    setIsLoaded(true);
                } catch (error) {
                    console.error("Error fetching warranties:", error);
                }
            }
        };

        fetchData();
    }, [dispatch, inputs, warrantyData, isLoaded]);

    useEffect(() => {
        if (usersData[0] === undefined) {
            getUsers();
        }
    }, [usersData]);

    const handleReturn = ({ id }) => {
        deleteWarranty({ id }, dispatch);
    };

    const handleComplete = ({ id }) => {
        completeWarranty({ id }, dispatch);
    };

    const handleInputStatus = (e) => {
        setIsLoaded(false);
        setInputs((prev) => {
            return { ...prev, page: 1, status: e.value };
        });
    };

    const handleResetFilters = () => {
        const principleParam = getPrincipleParam();
        setInputs({
            model: "",
            userId: specialAccess
                ? "63429be7a4b0715d61b8955c"
                : manager
                ? principle
                : staff
                ? principle
                : undefined,
            limit: 20,
            page: 1,
            status: "pending",
            serialNumber: "",
            dateTo: undefined,
            dateFrom: undefined,
            principle: principleParam,
        });
        setIsLoaded(false);
    };

    const columns = [
        {
            Header: "Attachment",
            accessor: "image", 
            Cell: ({ value }) => (
                <div className="flex justify-center">
                    <img 
                        className="max-w-8 max-h-8 cursor-pointer" 
                        src={value} 
                        alt={""}
                        onClick={() => window.open(value, '_blank')}
                    />
                </div>
            ),
        },
        {
            Header: "Message",
            accessor: "message",
            Cell: ({ value }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{value}</div>
            ),
        },
        {
            Header: "Order",
            accessor: "order",
            Cell: ({ value }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{value ? value.invoiceId : ""}</div>
            ),
        },
        {
            Header: "User",
            accessor: "user",
            Cell: ({ value }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{value ? value.name : ""}</div>
            ),
        },
        {
            Header: "Phone Number",
            accessor: "user.phoneNumber",
            Cell: ({ value, row }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.original.user ? row.original.user.phoneNumber : ""}</div>
            ),
        },
        {
            Header: "Request Date",
            accessor: "createdAt",
            Cell: ({ value }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {moment(value).format("DD/MM/YYYY")}
                </div>
            ),
        },
        {
            Header: "Status",
            accessor: "status",
            Cell: ({ value }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">{value}</div>
            ),
        },
        {
            Header: "Action",
            accessor: "action",
            Cell: (params) => {
                return (
                    <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex space-x-2">
                            {params.row.original.status === "pending" ? (
                                <>
                                    <button
                                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs"
                                        onClick={() => {
                                            setModalData(params.row.original._id);
                                            setCompletedWarrantyMenu(true);
                                        }}
                                    >
                                        Accept
                                    </button>
                                    <button
                                        className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs"
                                        onClick={() => {
                                            setModalData(params.row.original._id);
                                            setReturnWarrantyMenu(true);
                                        }}
                                    >
                                        Reject
                                    </button>
                                </>
                            ) : params.row.original.status === "in_progress" ? (
                                <button
                                    className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-xs"
                                    onClick={() => {
                                        setModalData(params.row.original._id);
                                        setCompletedWarrantyMenu(true);
                                    }}
                                >
                                    Complete
                                </button>
                            ) : null}
                        </div>
                    </div>
                );
            },
        },
    ];

    return (
        <div className="warranty-request-container">
            {/* Header Section */}
            <div className="warranty-header">
                <button
                    onClick={handleResetFilters}
                    className="warranty-reset-btn"
                >
                    <FiPlus className="mr-2" />
                    Reset Filters
                </button>
            </div>

            {/* Filter Panel */}
            <div className="warranty-filter-panel">
                <div className="warranty-filter-grid">
                    <div>
                        <label className="warranty-filter-label">
                            Status
                        </label>
                        <div className="react-select-container">
                            <Select
                                options={statusOptions.map((status, i) => {
                                    return {
                                        label: status.label,
                                        value: status.value,
                                    };
                                })}
                                onChange={handleInputStatus}
                                placeholder="Select Status"
                                {...commonSelectProps}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Desktop Table View */}
            <div className="warranty-table-container desktop-only">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-orange-400">
                        <tr>
                            {columns.map((column) => (
                                <th
                                    key={column.Header}
                                    className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider"
                                >
                                    {column.Header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {isLoaded && warrantyData ? warrantyData.map((warranty) => (
                            <tr key={warranty._id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    <div className="flex justify-center">
                                        <img 
                                            className="max-w-8 max-h-8 cursor-pointer" 
                                            src={warranty.image} 
                                            alt={""}
                                            onClick={() => window.open(warranty.image, '_blank')}
                                        />
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {warranty.message}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {warranty.order ? warranty.order.invoiceId : ""}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {warranty.user ? warranty.user.name : ""}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {warranty.user ? warranty.user.phoneNumber : ""}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {moment(warranty.createdAt).format("DD/MM/YYYY")}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                                    {warranty.status}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    <div className="flex space-x-2">
                                        {warranty.status === "pending" ? (
                                            <>
                                                <button
                                                    className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs"
                                                    onClick={() => {
                                                        setModalData(warranty._id);
                                                        setCompletedWarrantyMenu(true);
                                                    }}
                                                >
                                                    Accept
                                                </button>
                                                <button
                                                    className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs"
                                                    onClick={() => {
                                                        setModalData(warranty._id);
                                                        setReturnWarrantyMenu(true);
                                                    }}
                                                >
                                                    Reject
                                                </button>
                                            </>
                                        ) : warranty.status === "in_progress" ? (
                                            <button
                                                className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-xs"
                                                onClick={() => {
                                                    setModalData(warranty._id);
                                                    setCompletedWarrantyMenu(true);
                                                }}
                                            >
                                                Complete
                                            </button>
                                        ) : null}
                                    </div>
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={columns.length} className="px-6 py-4 text-center text-gray-500">
                                    Loading...
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                
                {/* Desktop Pagination */}
                {isLoaded && warranties && (() => {
                    const totalCount = warranties.totalPages || 0;
                    const totalPages = Math.ceil(totalCount / inputs.limit) || 1;
                    const isFirstPage = inputs.page === 1;
                    const isLastPage = inputs.page >= totalPages;
                    
                    return (
                        <div className="order-list-table-footer warranty-pagination desktop-pagination px-4 py-3 flex items-center justify-between border-t border-gray-200">
                            <div className="order-list-table-footer-inner flex-1 flex justify-between items-center">
                                <div className="order-list-table-footer-results warranty-pagination-info text-sm text-gray-700">
                                    Showing{' '}
                                    <span className="font-medium">
                                        {((inputs.page - 1) * inputs.limit) + 1}
                                    </span>
                                    {' '}-{' '}
                                    <span className="font-medium">
                                        {Math.min(inputs.page * inputs.limit, totalCount)}
                                    </span>
                                    {' '}of{' '}
                                    <span className="font-medium">{totalCount}</span>
                                    {' '}results
                                </div>
                                <div className="order-list-table-footer-pagination flex space-x-2">
                                    <button
                                        onClick={() => setInputs(prev => ({ ...prev, page: 1 }))}
                                        disabled={isFirstPage}
                                        title="First"
                                        aria-label="First page"
                                        className={`order-list-pagination-btn px-3 py-1 rounded-md text-sm font-medium ${
                                            isFirstPage
                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                : 'bg-orange-400 text-white hover:bg-orange-500'
                                        }`}
                                    >
                                        <span className="order-list-pagination-icon" aria-hidden>&lt;&lt;</span>
                                        <span className="order-list-pagination-text">First</span>
                                    </button>
                                    <button
                                        onClick={() => setInputs(prev => ({ ...prev, page: prev.page - 1 }))}
                                        disabled={isFirstPage}
                                        title="Previous"
                                        aria-label="Previous page"
                                        className={`order-list-pagination-btn px-3 py-1 rounded-md text-sm font-medium ${
                                            isFirstPage
                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                : 'bg-orange-400 text-white hover:bg-orange-500'
                                        }`}
                                    >
                                        <span className="order-list-pagination-icon" aria-hidden>&lt;</span>
                                        <span className="order-list-pagination-text">Previous</span>
                                    </button>
                                    <span className="order-list-pagination-page px-3 py-1 bg-gray-100 text-gray-900 rounded-md text-sm font-medium">
                                        Page {inputs.page} of {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setInputs(prev => ({ ...prev, page: prev.page + 1 }))}
                                        disabled={isLastPage}
                                        title="Next"
                                        aria-label="Next page"
                                        className={`order-list-pagination-btn px-3 py-1 rounded-md text-sm font-medium ${
                                            isLastPage
                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                : 'bg-orange-400 text-white hover:bg-orange-500'
                                        }`}
                                    >
                                        <span className="order-list-pagination-icon" aria-hidden>&gt;</span>
                                        <span className="order-list-pagination-text">Next</span>
                                    </button>
                                    <button
                                        onClick={() => setInputs(prev => ({ ...prev, page: totalPages }))}
                                        disabled={isLastPage}
                                        title="Last"
                                        aria-label="Last page"
                                        className={`order-list-pagination-btn px-3 py-1 rounded-md text-sm font-medium ${
                                            isLastPage
                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                : 'bg-orange-400 text-white hover:bg-orange-500'
                                        }`}
                                    >
                                        <span className="order-list-pagination-icon" aria-hidden>&gt;&gt;</span>
                                        <span className="order-list-pagination-text">Last</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </div>

            {/* Mobile Card View */}
            <div className="warranty-cards-container mobile-only">
                {isLoaded && warrantyData ? warrantyData.map((warranty) => (
                    <div key={warranty._id} className="warranty-card">
                        {/* Image Section */}
                        <div className="warranty-card-image-section">
                            <img 
                                className="warranty-card-image" 
                                src={warranty.image} 
                                alt={"Warranty attachment"}
                                onClick={() => window.open(warranty.image, '_blank')}
                            />
                        </div>

                        {/* Info Section */}
                        <div className="warranty-card-info">
                            <div className="warranty-card-row">
                                <span className="warranty-card-label">Status:</span>
                                <span className={`warranty-card-status warranty-card-status-${warranty.status}`}>
                                    {warranty.status}
                                </span>
                            </div>

                            <div className="warranty-card-row">
                                <span className="warranty-card-label">Date:</span>
                                <span className="warranty-card-value">
                                    {moment(warranty.createdAt).format("DD/MM/YYYY")}
                                </span>
                            </div>

                            <div className="warranty-card-row">
                                <span className="warranty-card-label">Order ID:</span>
                                <span className="warranty-card-value">
                                    {warranty.order ? warranty.order.invoiceId : "N/A"}
                                </span>
                            </div>

                            <div className="warranty-card-row">
                                <span className="warranty-card-label">Customer:</span>
                                <span className="warranty-card-value">
                                    {warranty.user ? warranty.user.name : "N/A"}
                                </span>
                            </div>

                            <div className="warranty-card-row">
                                <span className="warranty-card-label">Phone:</span>
                                <span className="warranty-card-value">
                                    {warranty.user ? warranty.user.phoneNumber : "N/A"}
                                </span>
                            </div>

                            <div className="warranty-card-message">
                                <span className="warranty-card-label">Message:</span>
                                <p className="warranty-card-message-text">
                                    {warranty.message}
                                </p>
                            </div>

                            {/* Action Buttons */}
                            {(warranty.status === "pending" || warranty.status === "in_progress") && (
                                <div className="warranty-card-actions">
                                    {warranty.status === "pending" ? (
                                        <>
                                            <button
                                                className="warranty-card-btn warranty-card-btn-accept"
                                                onClick={() => {
                                                    setModalData(warranty._id);
                                                    setCompletedWarrantyMenu(true);
                                                }}
                                            >
                                                Accept
                                            </button>
                                            <button
                                                className="warranty-card-btn warranty-card-btn-reject"
                                                onClick={() => {
                                                    setModalData(warranty._id);
                                                    setReturnWarrantyMenu(true);
                                                }}
                                            >
                                                Reject
                                            </button>
                                        </>
                                    ) : warranty.status === "in_progress" ? (
                                        <button
                                            className="warranty-card-btn warranty-card-btn-complete"
                                            onClick={() => {
                                                setModalData(warranty._id);
                                                setCompletedWarrantyMenu(true);
                                            }}
                                        >
                                            Complete
                                        </button>
                                    ) : null}
                                </div>
                            )}
                        </div>
                    </div>
                )) : (
                    <div className="warranty-loading">
                        Loading...
                    </div>
                )}

                {/* Mobile Pagination */}
                {isLoaded && warranties && (() => {
                    const totalCount = warranties.totalPages || 0;
                    const totalPages = Math.ceil(totalCount / inputs.limit) || 1;
                    const isFirstPage = inputs.page === 1;
                    const isLastPage = inputs.page >= totalPages;
                    
                    return (
                        <div className="order-list-table-footer warranty-pagination mobile-pagination px-4 py-3 flex items-center justify-between border-t border-gray-200">
                            <div className="order-list-table-footer-inner flex-1 flex justify-between items-center">
                                <div className="order-list-table-footer-results warranty-pagination-info-mobile text-sm text-gray-700">
                                    Showing{' '}
                                    <span className="font-medium">
                                        {((inputs.page - 1) * inputs.limit) + 1}
                                    </span>
                                    {' '}-{' '}
                                    <span className="font-medium">
                                        {Math.min(inputs.page * inputs.limit, totalCount)}
                                    </span>
                                    {' '}of{' '}
                                    <span className="font-medium">{totalCount}</span>
                                    {' '}results
                                </div>
                                <div className="order-list-table-footer-pagination flex space-x-2">
                                    <button
                                        onClick={() => setInputs(prev => ({ ...prev, page: 1 }))}
                                        disabled={isFirstPage}
                                        title="First"
                                        aria-label="First page"
                                        className={`order-list-pagination-btn px-3 py-1 rounded-md text-sm font-medium ${
                                            isFirstPage
                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                : 'bg-orange-400 text-white hover:bg-orange-500'
                                        }`}
                                    >
                                        <span className="order-list-pagination-icon" aria-hidden>&lt;&lt;</span>
                                        <span className="order-list-pagination-text">First</span>
                                    </button>
                                    <button
                                        onClick={() => setInputs(prev => ({ ...prev, page: prev.page - 1 }))}
                                        disabled={isFirstPage}
                                        title="Previous"
                                        aria-label="Previous page"
                                        className={`order-list-pagination-btn px-3 py-1 rounded-md text-sm font-medium ${
                                            isFirstPage
                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                : 'bg-orange-400 text-white hover:bg-orange-500'
                                        }`}
                                    >
                                        <span className="order-list-pagination-icon" aria-hidden>&lt;</span>
                                        <span className="order-list-pagination-text">Previous</span>
                                    </button>
                                    <span className="order-list-pagination-page px-3 py-1 bg-gray-100 text-gray-900 rounded-md text-sm font-medium">
                                        Page {inputs.page} of {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setInputs(prev => ({ ...prev, page: prev.page + 1 }))}
                                        disabled={isLastPage}
                                        title="Next"
                                        aria-label="Next page"
                                        className={`order-list-pagination-btn px-3 py-1 rounded-md text-sm font-medium ${
                                            isLastPage
                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                : 'bg-orange-400 text-white hover:bg-orange-500'
                                        }`}
                                    >
                                        <span className="order-list-pagination-icon" aria-hidden>&gt;</span>
                                        <span className="order-list-pagination-text">Next</span>
                                    </button>
                                    <button
                                        onClick={() => setInputs(prev => ({ ...prev, page: totalPages }))}
                                        disabled={isLastPage}
                                        title="Last"
                                        aria-label="Last page"
                                        className={`order-list-pagination-btn px-3 py-1 rounded-md text-sm font-medium ${
                                            isLastPage
                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                : 'bg-orange-400 text-white hover:bg-orange-500'
                                        }`}
                                    >
                                        <span className="order-list-pagination-icon" aria-hidden>&gt;&gt;</span>
                                        <span className="order-list-pagination-text">Last</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </div>

            {/* Modals */}
            {returnWarrantyMenu && (
                <div className="overlayContainer">
                    <div className="center">
                        <div
                            className="close"
                            onClick={() => setReturnWarrantyMenu(false)}
                        >
                            <Close></Close>
                        </div>
                        <div className="actionContainer">
                            <div className="updateOrder">
                                <h3 className="updateProductTitle">
                                    Reject Warranty Request?
                                </h3>
                                <div className="buttonContainer">
                                    <button
                                        className="confirm pointer red"
                                        onClick={() => {
                                            handleReturn({
                                                id: modalData,
                                            });
                                            setReturnWarrantyMenu(false);
                                        }}
                                    >
                                        Confirm
                                    </button>
                                    <button
                                        className="cancel pointer"
                                        onClick={() => {
                                            setReturnWarrantyMenu(false);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {completedWarrantyMenu && (
                <div className="overlayContainer">
                    <div className="center">
                        <div
                            className="close"
                            onClick={() => setCompletedWarrantyMenu(false)}
                        >
                            <Close></Close>
                        </div>
                        <div className="actionContainer">
                            <div className="updateOrder">
                                <h3 className="updateProductTitle">
                                    Complete Warranty Request?
                                </h3>
                                <div className="buttonContainer">
                                    <button
                                        className="confirm pointer green"
                                        onClick={() => {
                                            handleComplete({
                                                id: modalData,
                                            });
                                            setCompletedWarrantyMenu(false);
                                        }}
                                    >
                                        Confirm
                                    </button>
                                    <button
                                        className="cancel pointer"
                                        onClick={() => {
                                            setCompletedWarrantyMenu(false);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
} 